import { useQuery } from "@tanstack/react-query";
import { BookX, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface UnknownBooksResponse {
  count: number;
  books: Array<{
    book_id: number;
    project_name: string;
    category: string;
  }>;
}

/** 책장 미소속 책 목록 위젯. */
export function UnknownBooksCard() {
  const { data, isLoading } = useQuery<UnknownBooksResponse>({
    queryKey: ["admin", "unknown-books"],
    queryFn: () => api.get("/admin/unknown-books"),
    refetchInterval: 120_000,
  });

  if (isLoading || !data) {
    return <Skeleton className="h-40" />;
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <BookX className="h-4 w-4 text-orange-500" />
          <h3 className="text-sm font-semibold">
            미분류 책 ({data.count}건)
          </h3>
        </div>

        {data.count === 0 ? (
          <div className="text-xs text-gray-500 py-2">
            ✓ 모든 책이 책장에 정상 배치되어 있습니다.
          </div>
        ) : (
          <>
            <div className="text-xs text-orange-700 mb-2">
              아래 책을 BookStack 책장에 배치하면 검색에 포함됩니다.
            </div>
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {data.books.map((b) => (
                <li
                  key={b.book_id}
                  className="text-xs flex items-center justify-between p-2 bg-orange-50 rounded"
                >
                  <span className="truncate">
                    <span className="font-medium">{b.project_name}</span>
                    <span className="text-gray-500 ml-2">id={b.book_id}</span>
                  </span>
                  <a
                    href={`https://arkivekb.pilgrimscenter.org/books/${b.book_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline ml-2 flex-shrink-0"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
